import {announce} from '@react-aria/live-announcer'
import {Button, Dialog, DialogTrigger, Heading, Modal, ModalOverlay} from 'react-aria-components'
import {toast} from 'sonner'

import OrderItemGroups from '@/components/OrderItemGroups/OrderItemGroups'
import {useLocale} from '@/lib/locale/locale'
import type {CartItem} from '@/models/cartModel'
import {type Order, type OrderItem, OrderItemStatus} from '@/models/orderModel'
import {returnOrder} from '@/services/orderService'
import toastErrorMessage from '@/utils/errors/toastErrorMessage'

import style from './ReturnDialog.module.scss'

interface Props {
  order: Order
  onOpenChange?: (isOpen: boolean) => void
}

const isItemDisabled = (item: OrderItem) => item.status !== OrderItemStatus.DELIVERED

// TODO: merge with logout dialog to a dialog component
const ReturnDialog = deepMemo<PropsWithChildren<Props>>(function ReturnDialog({
  children,
  order,
  onOpenChange,
}) {
  const [locale] = useLocale()
  const selection = useRef(new Map<string, boolean>())

  const onConfirm = useCallback(
    (action: () => void) => {
      const itemsToReturn = Array.from(selection.current.entries())
        .filter(([, value]) => value)
        .map(([key]) => key)

      if (!itemsToReturn.length) {
        toastErrorMessage('Please select at least one item to return.')
        return
      }

      // TODO: convert to mutation
      void returnOrder({
        locale,
        orderId: order.id,
        lineItems: order.lineItems.filter(item =>
          itemsToReturn.includes(`${item.retailer}-${item.id}`),
        ),
      })
        .then(result => {
          if (result === true) {
            toast.success('Return requested!')
            announce('Return requested!')
            action()
          } else {
            toastErrorMessage(result)
          }
        })
        .catch(() => {
          toastErrorMessage('Something went wrong, please try again later.')
        })
    },
    [order.id, order.lineItems, locale],
  )

  const onSelect = useCallback((item: CartItem, state: boolean) => {
    selection.current.set(`${item.retailer}-${item.id}`, state)
  }, [])

  return (
    <DialogTrigger onOpenChange={onOpenChange}>
      {children}
      <ModalOverlay
        // TODO: add animation
        className={({isEntering, isExiting}) =>
          clsx(style.Overlay, isEntering && style.IsEntering, isExiting && style.IsExiting)
        }
        isDismissable
      >
        <Modal className={style.Modal}>
          <Dialog className={style.Dialog}>
            {({close}) => (
              <>
                <Heading className={style.Heading} slot='title'>
                  Select return items
                </Heading>
                <div className={style.Content}>
                  {/* <div className={style.OrderId}>{`Order #${order.id}`}</div> */}
                  <OrderItemGroups
                    isSelectable
                    onSelect={onSelect}
                    items={order.lineItems}
                    isItemDisabled={isItemDisabled}
                    checkboxLabel='Return'
                  />
                </div>
                <div className={style.Action}>
                  <Button className={style.Cancel} onPress={close}>
                    Cancel
                  </Button>
                  <Button
                    className={style.Ok}
                    onPress={() => {
                      onConfirm(close)
                    }}
                  >
                    Confirm
                  </Button>
                </div>
              </>
            )}
          </Dialog>
        </Modal>
      </ModalOverlay>
    </DialogTrigger>
  )
})()

export default ReturnDialog
